import { FC, useCallback, useMemo } from "react";
import DataTable, { CellSize } from "../table";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowParams } from "@mui/x-data-grid";
import { Link, Tooltip, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { cacheService, hlsChunkerService, mediaService } from "../../../Core/Api/services";
import { useDispatch } from "react-redux";
import {
    DeleteAction,
    GetFps,
    GetLinkedToPreviewActionId,
    GetSelectedAsset,
    GetTimelineActions,
    SetLinkedToPreviewActionId,
    SetPreviewIsPlaying,
    SetPreviewSrc,
    SetPreviewTime,
    SetSelectedAsset,
    SetSelectedAssetFile,
    SetShowSequence,
} from "../../../Redux/Reducers/redux-slice-timetool";
import { useSelector } from "react-redux";
import { SecToTimecode } from "../../../Utils/Timecode/ReactPlayer";
import { routePaths } from "../../../Router/router";
import { useTranslation } from "react-i18next";

interface TimetoolActionsListTableProp { }

const TimetoolActionsListTable: FC<TimetoolActionsListTableProp> = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const orderedActions = useSelector(GetTimelineActions)
        .map((x) => x.actions)
        .flat(1)
        .sort((a, b) => a.start - b.start);
    const fps = useSelector(GetFps);
    type Row = (typeof rows)[number];

    const selectedAsset = useSelector(GetSelectedAsset);
    const linkedAction = useSelector(GetLinkedToPreviewActionId);
    const rows: any = useSelector(GetTimelineActions)
        .map((x) =>
            x.actions.map((a) => ({
                ...a.data,
                start: a.start,
                end: a.end,
                actionId: a.id,
                assetId: a.data.id,
                id: a.data.actionId,
            }))
        )
        .flat(1);

    const getSelectedRows = useMemo(() => {
        return linkedAction !== ""
            ? rows.findIndex((x) => x.id === linkedAction) >= 0
                ? [rows.find((x) => x.id === linkedAction).id]
                : []
            : rows.findIndex((x) => x.assetId === selectedAsset?.id) >= 0
                ? [rows.find((x) => x.assetId === selectedAsset?.id).id]
                : [];
    }, [selectedAsset?.id, linkedAction]);

    const handleSelectAsset = useCallback((id: string, actionId: number) => {
        mediaService.GetFilesByObjectId(parseInt(id)).then((res) => {
            const selectedFile = res.files?.find((x) => x.category === "Proxy");
            if (!selectedFile) return;
            dispatch(SetShowSequence(false));
            dispatch(SetSelectedAssetFile(selectedFile));
            dispatch(SetSelectedAsset(rows.find((x) => x.assetId === id)));
            dispatch(
                SetPreviewSrc(
                    selectedFile?.id
                        ? `${hlsChunkerService.GetServiceUrl()}/${selectedFile.id}/${cacheService.appData?.platformId}`
                        : ""
                )
            );
            dispatch(SetPreviewIsPlaying(false));
            dispatch(SetPreviewTime(0));
            dispatch(SetLinkedToPreviewActionId(actionId?.toString() || ""));
        });
    }, []);

    const cols: GridColDef[] = useMemo(
        () => [
            {
                flex: CellSize.small,
                minWidth: CellSize.small,
                field: "assetId",
                headerName: t("common:assetId"),
                renderCell: (data: Row) => (
                    <Link href={routePaths.asset.path + "/" + data.value} target="_blank" underline="hover">
                        {data.value}
                    </Link>
                ),
            },
            {
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                field: "name",
                headerName: t("common:name"),
                renderCell: (data: Row) => (
                    <Typography whiteSpace="normal" textOverflow="clip" variant="body2">
                        {data.value}
                    </Typography>
                ),
            },
            {
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                field: "duration",
                headerName: t("common:duration"),
                valueGetter: (data: Row, row: any) => {
                    if (!row) return;
                    return `${SecToTimecode(Math.round(row?.end - row?.start), 25)}`;
                },
                renderCell: (data: Row) => <Typography>{data.value}</Typography>,
            },
            {
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                field: "start",
                headerName: t("table:headerName.clipStart"),
                valueGetter: (data: Row, row: any) => {
                    if (!row) return;
                    return SecToTimecode(row?.offSet / fps, fps);
                },
            },
            {
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                field: "out",
                headerName: t("table:headerName.clipEnd"),
                valueGetter: (data: Row, row: any) => {
                    if (!row) return;
                    return SecToTimecode(row?.end - row?.start + row?.offSet / fps, fps);
                },
            },
            {
                flex: CellSize.actionsDefault,
                minWidth: CellSize.actionsDefault,
                field: "actions",
                type: "actions",
                align: "right",
                getActions: (params: GridRowParams) => [
                    <Tooltip title={t("common:preview")}>
                        <GridActionsCellItem
                            icon={<VisibilityIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                            label={t("common:preview")}
                            onClick={(e) => {
                                handleSelectAsset(params?.row?.assetId, params?.row?.actionId);
                            }}
                        />
                    </Tooltip>,
                    <Tooltip title={t("tooltip:title.deleteClip")}>
                        <GridActionsCellItem
                            icon={<GridDeleteIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                            label={t("common:delete")}
                            onClick={(e) => dispatch(DeleteAction(params?.row?.actionId))}
                        />
                    </Tooltip>,
                ],
            },
        ],
        [rows, orderedActions]
    );

    return (
        <DataTable
            noControls
            selectedRows={getSelectedRows}
            pageSize={500}
            rows={rows}
            columns={cols}
            columnHeaderHeight={32}
        />
    );
};

export default TimetoolActionsListTable;
